/**
 * A render error must never leave a device showing nothing.
 *
 * React unmounts the whole tree when a render throws, and on a phone in a dark
 * room that is indistinguishable from a broken game: the player sits looking at
 * a blank screen while the projector carries on. This boundary keeps the failure
 * visible and recoverable — the player is told to reload, and the facilitator can
 * see that the device failed rather than that the player is idle.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@tq/ui";

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  message: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Left in the console deliberately: it is the only diagnostic available on a
    // phone at an event.
    console.error("[trustee-quest] render failed", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100dvh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "var(--page-bg)",
          color: "var(--ink)",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontSize: 22, margin: "0 0 8px" }}>This screen stopped working</h1>
          <p style={{ margin: "0 0 16px", color: "var(--muted)", fontWeight: 600 }}>
            Reload to rejoin. Your score and your place in the game are kept on the server.
          </p>
          <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <p style={{ margin: "16px 0 0", fontSize: 12, color: "var(--muted)" }}>
            {this.state.message}
          </p>
        </div>
      </div>
    );
  }
}
